import React from "react";
import PublicLayout from "@/components/PublicLayout";

const POLICIES = {
  terms: {
    title: "Terms of Service",
    sections: [
      ["1. Acceptance", "By using Vendora, you agree to be bound by these Terms. If you do not agree, do not use the platform."],
      ["2. Eligibility", "You must be at least 18 years old and able to enter into a binding contract under Canadian law."],
      ["3. Accounts", "You are responsible for the activity that occurs under your account. Keep your password confidential."],
      ["4. Acceptable Use", "Don't use Vendora to sell illegal products, infringe IP, or harass other users."],
      ["5. Payments", "All customer payments flow through Vendora's centralised accounts. Payouts to sellers, affiliates, and providers are made via internal ledger after the configured hold period."],
      ["6. Fees", "The default platform fee is 5% of the order total. Refunds and chargebacks are deducted from your balance."],
      ["7. Termination", "We may suspend or terminate your account for material breach. You may close your account at any time."],
      ["8. Governing Law", "These Terms are governed by the laws of Ontario, Canada."],
    ],
  },
  privacy: {
    title: "Privacy Policy",
    sections: [
      ["What we collect", "Account info, order data, addresses, and platform usage. We never sell your data."],
      ["How we use it", "To run the platform, process payments, and prevent fraud."],
      ["Cookies", "We use cookies for sessions and analytics. See our Cookie Policy."],
      ["Your rights", "You can request access, correction, or deletion of your personal data at hello@vendora.ca."],
      ["PIPEDA", "Vendora complies with Canadian PIPEDA requirements."],
    ],
  },
  cookies: {
    title: "Cookie Policy",
    sections: [
      ["Essential cookies", "Required for authentication and cart functionality."],
      ["Analytics", "Aggregated and anonymized usage data for product improvements."],
      ["Marketing", "Only set with your consent."],
    ],
  },
  refund: {
    title: "Refund Policy",
    sections: [
      ["30-day window", "Eligible products can be returned within 30 days of delivery."],
      ["Final sale", "Items marked Final Sale are non-refundable."],
      ["Process", "Open a return from your order page. We'll email a return label."],
      ["Centralised refunds", "Refunds are processed through Vendora's centralised account and deducted from the seller's ledger."],
    ],
  },
  shipping: {
    title: "Shipping Policy",
    sections: [
      ["Carriers", "We ship via Royal Mail, DPD and Evri based on destination and weight."],
      ["Delivery times", "Most UK mainland orders arrive in 2-5 working days. The Highlands and Islands, Northern Ireland and offshore addresses may take longer."],
      ["Tracking", "Tracking numbers are emailed when labels are created."],
      ["Free delivery", "Orders of £50 or more ship free within the UK. Below that, delivery is £4.95."],
    ],
  },
  vendor: {
    title: "Vendor Agreement",
    sections: [
      ["Eligibility", "Vendors must be registered Canadian businesses with valid GST/HST numbers (where applicable)."],
      ["Product standards", "All listings must comply with Health Canada, CSA, and Vendora content guidelines."],
      ["Pricing", "Vendors set their own prices. Vendora collects a 5% platform fee from each sale."],
      ["Fulfillment SLA", "Orders must be acknowledged in 24h and shipped in 48h."],
    ],
  },
  supplier: {
    title: "Supplier Agreement",
    sections: [
      ["Inventory", "Suppliers must maintain accurate inventory counts. Overselling more than 5% in a quarter triggers review."],
      ["Quality", "Products are subject to admin review. Repeated quality complaints result in delisting."],
      ["Payouts", "Earnings are credited to your wallet immediately and become available after the 7-day hold."],
    ],
  },
  affiliate: {
    title: "Affiliate Agreement",
    sections: [
      ["Commission", "10% of the order total, paid in GBP."],
      ["Attribution", "Last-click attribution within a 30-day cookie window."],
      ["Prohibited methods", "No spam, no bidding on branded keywords, no incentivized clicks."],
      ["Payouts", "Minimum £50 balance. UK bank transfer or PayPal."],
    ],
  },
  serviceprovider: {
    title: "Service Provider Agreement",
    sections: [
      ["Verification", "All providers must verify identity, professional credentials (where applicable), and insurance."],
      ["Quality", "Maintain a 4.5+ star rating to stay featured. Multiple complaints trigger review."],
      ["Billing", "Vendora collects payments from clients and remits to providers after the hold period."],
    ],
  },
};

export default function Legal({ slug }) {
  const p = POLICIES[slug] || { title: "Document", sections: [] };
  return (
    <PublicLayout>
      <section className="max-w-3xl mx-auto px-6 py-16">
        <div className="label-eyebrow text-secondary mb-2">Legal · Vendora</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary leading-none">{p.title}</h1>
        <p className="mt-2 text-xs text-muted-foreground font-mono">Last updated: February 2026</p>
        <div className="mt-10 space-y-8">
          {p.sections.map(([h, body]) => (
            <div key={h}>
              <h2 className="font-display text-xl font-semibold text-primary">{h}</h2>
              <p className="mt-2 text-muted-foreground leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>
    </PublicLayout>
  );
}
